/* eslint-disable camelcase */
import { Button } from '@fountain/fountain-ui-components';
import { Box, Grid, InputLabel, TextField } from '@material-ui/core';
import {
  CancelablePromise,
  OAuth2Configuration,
  OAuth20Service,
} from 'api-clients/monolith';
import { useForm } from 'hooks';
import React, { useCallback, useEffect, useState, VFC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Error } from 'components/Error';
import { Loading } from 'components/Loading';
import { addMessageAction } from 'containers/FlashMessage/actions';
import { useApiServiceMutation } from 'hooks/useApiServiceMutation';
import globalMessages from 'shared/global/messages';

import { messages } from './messages';
import { useStyles } from './styles';

export interface FormValues {
  id: string;
  name: string;
  client_key: string;
  client_secret: string;
  token_url: string;
}

const EMPTY_VALUES: FormValues = {
  id: '',
  name: '',
  client_key: '',
  client_secret: '',
  token_url: '',
};

const toFormValues = (config?: OAuth2Configuration | null): FormValues => ({
  id: config?.id ?? '',
  name: config?.name ?? '',
  client_key: config?.client_key ?? '',
  client_secret: config?.client_secret ?? '',
  token_url: config?.token_url ?? '',
});

const toPayload = (values: FormValues): Omit<FormValues, 'id'> => ({
  name: values.name,
  client_key: values.client_key,
  client_secret: values.client_secret,
  token_url: values.token_url,
});

interface OAuth2ConfigurationFormContentProps {
  initialValues?: FormValues;
}

export const OAuth2ConfigurationFormContent: VFC<
  OAuth2ConfigurationFormContentProps
> = ({ initialValues }) => {
  const intl = useIntl();
  const styles = useStyles();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);
  const [hasClientSecretBeenFocused, setHasClientSecretBeenFocused] =
    useState(false);
  const [prevClientSecret, setPrevClientSecret] = useState<string | null>(null);

  const validate = (values: FormValues) => {
    const errors: Partial<Record<keyof FormValues, string>> = {};

    if (!values.name.trim()) {
      errors.name = intl.formatMessage(messages.nameRequired);
    }
    if (!values.client_key.trim()) {
      errors.client_key = intl.formatMessage(messages.clientKeyRequired);
    }
    if (!values.client_secret.trim()) {
      errors.client_secret = intl.formatMessage(messages.clientSecretRequired);
    }
    if (!values.token_url.trim()) {
      errors.token_url = intl.formatMessage(messages.tokenUrlRequired);
    }

    return errors;
  };

  const makeSuccessHandler = (message: string) => () => {
    setIsLoading(false);
    setHasChanged(false);
    dispatch(addMessageAction(message));
  };

  const onSaveSuccess = makeSuccessHandler(
    intl.formatMessage(messages.saveSuccess),
  );

  const onUpdateSuccess = makeSuccessHandler(
    intl.formatMessage(messages.updateSuccess),
  );

  const onSaveError = () => {
    setIsLoading(false);
  };

  const { mutation: updateOAuthConfig } = useApiServiceMutation<
    OAuth2Configuration,
    ({
      id,
      values,
    }: {
      id: string;
      values: Omit<FormValues, 'id'>;
    }) => CancelablePromise<OAuth2Configuration>
  >(
    ({ id, values }: { id: string; values: Omit<FormValues, 'id'> }) =>
      OAuth20Service.putInternalApiOauth2Configurations(id, values),
    {
      onSuccess: onUpdateSuccess,
      onError: onSaveError,
    },
  );

  const { mutation: createOAuthConfig } = useApiServiceMutation<
    OAuth2Configuration,
    (values: Omit<FormValues, 'id'>) => CancelablePromise<OAuth2Configuration>
  >(
    (values: Omit<FormValues, 'id'>) =>
      OAuth20Service.postInternalApiOauth2Configurations(values),
    {
      onSuccess: onSaveSuccess,
      onError: onSaveError,
    },
  );

  const onSubmit = (values: FormValues) => {
    setIsLoading(true);

    const payload = toPayload(values);

    if (values.id) {
      updateOAuthConfig({ id: values.id, values: payload });
      return;
    }

    createOAuthConfig(payload);
  };

  const { values, handleChange, handleSubmit, errors } = useForm<FormValues>(
    onSubmit,
    validate,
    initialValues ?? EMPTY_VALUES,
  );

  const onChange = useCallback(
    (params: Partial<FormValues>) => {
      const nextValues = { ...values, ...params };

      handleChange(params);
      setHasChanged(
        Object.values(nextValues).some(value => value.trim() !== ''),
      );
    },
    [handleChange, values],
  );

  const handleClientSecretFocus = () => {
    if (hasClientSecretBeenFocused) return;

    setPrevClientSecret(values.client_secret);
    onChange({ client_secret: '' });
    setHasClientSecretBeenFocused(true);
  };

  const handleClientSecretBlur = () => {
    if (!hasClientSecretBeenFocused) return;

    // If the user didn't enter anything, restore the previous secret
    if (!values.client_secret.trim()) {
      onChange({ client_secret: prevClientSecret ?? '' });
    }

    // Reset the temporary storage so future focuses behave the same way
    setPrevClientSecret(null);
    setHasClientSecretBeenFocused(false);
  };

  return (
    <Grid
      container
      direction="column"
      className={styles.rowContainer}
      spacing={2}
    >
      <form>
        <Grid item className={styles.textField}>
          <InputLabel htmlFor="name" className={styles.label}>
            {intl.formatMessage(messages.name)}
          </InputLabel>
          <TextField
            id="name"
            fullWidth
            margin="dense"
            variant="outlined"
            placeholder={intl.formatMessage(messages.namePlaceholder)}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              onChange({ name: event.target.value })
            }
            error={Boolean(errors.name)}
            value={values.name}
            inputProps={{ 'data-testid': 'textfield-name' }}
          />
          <Error error={errors.name} />
        </Grid>
        <Grid item className={styles.textField}>
          <InputLabel htmlFor="clientKey" className={styles.label}>
            {intl.formatMessage(messages.clientKey)}
          </InputLabel>
          <TextField
            id="clientKey"
            fullWidth
            margin="dense"
            variant="outlined"
            placeholder={intl.formatMessage(messages.clientKeyPlaceholder)}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              onChange({ client_key: event.target.value })
            }
            error={Boolean(errors.client_key)}
            value={values.client_key}
            inputProps={{ 'data-testid': 'textfield-clientKey' }}
          />
          <Error error={errors.client_key} />
        </Grid>
        <Grid item className={styles.textField}>
          <InputLabel htmlFor="clientSecret" className={styles.label}>
            {intl.formatMessage(messages.clientSecret)}
          </InputLabel>
          <TextField
            id="clientSecret"
            fullWidth
            margin="dense"
            variant="outlined"
            placeholder={intl.formatMessage(messages.clientSecretPlaceholder)}
            onFocus={handleClientSecretFocus}
            onBlur={handleClientSecretBlur}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              onChange({ client_secret: event.target.value })
            }
            error={Boolean(errors.client_secret)}
            value={values.client_secret}
            inputProps={{ 'data-testid': 'textfield-clientSecret' }}
          />
          <Error error={errors.client_secret} />
        </Grid>
        <Grid item className={styles.textField}>
          <InputLabel htmlFor="tokenUrl" className={styles.label}>
            {intl.formatMessage(messages.tokenUrl)}
          </InputLabel>
          <TextField
            id="tokenUrl"
            fullWidth
            margin="dense"
            variant="outlined"
            placeholder={intl.formatMessage(messages.tokenUrlPlaceholder)}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              onChange({ token_url: event.target.value })
            }
            error={Boolean(errors.token_url)}
            value={values.token_url}
            inputProps={{ 'data-testid': 'textfield-tokenUrl' }}
          />
          <Error error={errors.token_url} />
        </Grid>
        <div className={styles.footer}>
          <Button type="secondary">
            <FormattedMessage {...globalMessages.cancel} />
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            disabled={isLoading || !hasChanged}
          >
            <FormattedMessage {...globalMessages.save} />
          </Button>
        </div>
      </form>
    </Grid>
  );
};

export const OAuth2ConfigurationForm: VFC = () => {
  const [initialValues, setInitialValues] = useState<FormValues | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const promise =
      OAuth20Service.getInternalApiOauth2ConfigurationsShowLatest() as CancelablePromise<
        OAuth2Configuration | null | undefined
      >;

    promise
      .then(resp => {
        if (!isMounted) return;

        setInitialValues(toFormValues(resp));
      })
      .catch(() => {
        // Ignore initial load errors and keep empty defaults.
        if (!isMounted) return;

        setInitialValues(EMPTY_VALUES);
      })
      .finally(() => {
        if (!isMounted) return;

        setIsInitialLoading(false);
      });

    return () => {
      isMounted = false;
      promise.cancel?.();
    };
  }, []);

  if (isInitialLoading || !initialValues) {
    return (
      <Box display="flex" justifyContent="center">
        <Loading />
      </Box>
    );
  }

  return <OAuth2ConfigurationFormContent initialValues={initialValues} />;
};
